import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { Divider, Flex, VFlex } from '@/components/base';
import Input from '@/components/Inputs/Input';
import SelectPortal from '@/components/Select/SelectPortal';
import Tree from '@/components/Select/Tree';
import type { TreeModalProps } from '@/components/Select/types';
import useDropdownPos from '@/hooks/pure/useDropdownPos';

type ContentProps<T extends {}> = TreeModalProps<T> & {
  dropdownRef: (dropdownEl: HTMLDivElement | null) => void;
};

const TreeModal = <T extends {}>(props: TreeModalProps<T>) => {
  const { dropdownPos, setDropdownRef } = useDropdownPos(props.status, {
    el: props.triggerEl,
    options: props.modalOptions,
  });

  return (
    <SelectPortal
      close={props.close}
      style={{
        ...(props.pos || dropdownPos),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      status={props.status}
    >
      <Content {...props} dropdownRef={setDropdownRef} />
    </SelectPortal>
  );
};

const Content = <T extends {}>(props: ContentProps<T>) => {
  const debounce = useDebouncedCallback(props.onSearchChange, 300);
  return (
    <Wrapper style={props.style} ref={props.dropdownRef}>
      {props.header && (
        <Flex f={1} padding="8px 8px 0px">
          {props.header}
        </Flex>
      )}
      {props.searchable && (
        <Flex padding="8px 8px 0px">
          <Input
            style={{ width: '100%', height: 32 }}
            placeholder="Search..."
            name="searchValue"
            sizeType="m"
            onChange={(e) => {
              debounce(e.target.value);
            }}
          />
        </Flex>
      )}
      <TreeViewport>
        <Tree
          mode={props.mode}
          dataList={props.dataList}
          selectedList={props.values}
          onCheck={props.onCheck}
          loading={props.loading}
          updateParentChildNode={props.updateParentChildNode}
          paginatedOptions={props.paginatedOptions}
        />
      </TreeViewport>
      {props.footer && <Divider />}
      {props.footer}
    </Wrapper>
  );
};

const Wrapper = styled(VFlex)`
  box-sizing: border-box;
  min-width: 400px;
  max-width: 600px;
  min-height: 0;
  overflow: hidden;
`;

const TreeViewport = styled.div`
  display: flex;
  flex: 1 1 300px;
  min-height: 0;
  max-height: 300px;
  overflow: hidden;
`;

export default TreeModal;
